import _FormControl from "@mui/material/FormControl";
import _Select from "@mui/material/Select";
import _MenuItem from "@mui/material/MenuItem";
import _CircularProgress from "@mui/material/CircularProgress";
import _Chip from "@mui/material/Chip";
import _Close from "@mui/icons-material/Close";
import _Tabs from "@mui/material/Tabs";
import _Tab from "@mui/material/Tab";
import _Typography from "@mui/material/Typography";
import _Box from "@mui/material/Box";
import _Button2 from "@mui/material/Button";
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useCancellablePromise, useFileSaver, useUnits } from '../../../hooks';
import { useAmplitudeTracker } from '../../../hooks/tracker';
import useUserGeogroupsQuery from '../../../hooks/use-user-geogroups-query';
import { GeogroupService } from '../../../services';
import { ExportIcon, GlobeIcon, PeopleIcon } from '../../icons';
import IndividualLeaderboard from './individual';
import StatCard from './stat-card';
import SubgroupsLeaderboard, { fakeSites, fakeTeams, fakeRegions } from './subgroups';
// The `rank_field` API values, grouped by what they measure — each group with a commuting
// equivalent gets a "vélotaf / tous les trajets" toggle; the others don't (no such variant).
const rankFieldGroupsConfig = {
  points: {
    field: 'progress_value',
    labelKey: 'commons.challenges.leaderboard.rank_field_type_points'
  },
  days: {
    field: 'count_cycling_days',
    commutingField: 'count_commuting_days',
    labelKey: 'commons.challenges.leaderboard.rank_field_type_days'
  },
  trips: {
    field: 'count_trace',
    commutingField: 'count_commuting_trace',
    labelKey: 'commons.challenges.leaderboard.rank_field_type_trips'
  },
  distance: {
    field: 'sum_trace_distance_m',
    commutingField: 'sum_commuting_trace_distance_m',
    labelKey: 'commons.challenges.leaderboard.rank_field_type_distance'
  },
  duration: {
    field: 'sum_trace_duration_s',
    labelKey: 'commons.challenges.leaderboard.rank_field_type_duration'
  },
  elevation: {
    field: 'sum_trace_positive_denivele_m',
    labelKey: 'commons.challenges.leaderboard.rank_field_type_elevation'
  }
};
const rankFieldGroups = Object.keys(rankFieldGroupsConfig);
const rankFieldDropdownSx = {
  minWidth: 220,
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    borderRadius: 2
  },
  '& .MuiSelect-select': {
    paddingBottom: 1,
    paddingTop: 1
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#dde0e7'
  }
};
const rankFieldDropdownMenuProps = {
  slotProps: {
    paper: {
      sx: {
        borderRadius: 1,
        boxShadow: '0px 2px 11px 0px rgba(204, 210, 222, 0.91)',
        '& .MuiMenuItem-root.Mui-selected': {
          bgcolor: '#d9e7ff',
          '&:hover': {
            bgcolor: '#d9e7ff'
          }
        }
      }
    }
  }
};
function getRankFieldExportColumn(group) {
  switch (group) {
    case 'distance':
      return {
        header: 'Distance (km)',
        formatValue: value => (value / 1000).toFixed(1)
      };
    case 'duration':
      return {
        header: 'Durée (h)',
        formatValue: value => (value / 3600).toFixed(1)
      };
    case 'days':
      return {
        header: 'Jours',
        formatValue: value => value
      };
    case 'trips':
      return {
        header: 'Trajets',
        formatValue: value => value
      };
    case 'elevation':
      return {
        header: 'Dénivelé (m)',
        formatValue: value => Math.round(value)
      };
    case 'points':
    default:
      return {
        header: 'Score (pts)',
        formatValue: value => value
      };
  }
}
const displayTypeToMod = {
  individual: 'cyclists',
  companySite: 'sites',
  companyTeam: 'teams',
  companyRegion: 'regions',
  companySubsidiary: 'entities'
};
const displayTypesMap = {
  individual: {
    labelKey: 'commons.challenges.leaderboard.by_cyclists'
  },
  companySite: {
    labelKey: 'commons.challenges.leaderboard.by_sites'
  },
  companyTeam: {
    labelKey: 'commons.challenges.leaderboard.by_teams'
  },
  companyRegion: {
    labelKey: 'commons.challenges.leaderboard.by_regions'
  },
  companySubsidiary: {
    labelKey: 'commons.challenges.leaderboard.by_subsidiaries'
  }
};
function CompanyLeaderboard({
  Button: _Button,
  FakeItemsCard,
  partner,
  isFree,
  isHolding,
  disableLeaderboardRestriction,
  currentUser,
  event,
  freeOfferEmployeesLeaderboardsMaxRowsCount,
  freeOfferSubgroupsLeaderboardsMaxRowsCount,
  challenge,
  displayTypes,
  employee,
  ExportDialog,
  onExportReady,
  sites,
  teams,
  regions,
  subsidiaries
}) {
  const [initialized, setInitialized] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayType, setDisplayType] = useState(() => {
    const param = searchParams.get('display');
    return param && displayTypes.includes(param) ? param : 'individual';
  });
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('display', displayType);
    setSearchParams(params, {
      replace: true
    });
    return () => {
      const params = new URLSearchParams(window.location.search);
      params.delete('display');
      setSearchParams(params, {
        replace: true
      });
    };
  }, [displayType]);
  const [selectedSubgroup, setSelectedSubgroup] = useState(null);
  const [page, setPage] = useState(1);
  const [individualLeaderboard, setIndividualLeaderboard] = useState();
  const [userChallenge, setUserChallenge] = useState();
  const [subgroupsLeaderboard, setSubgroupsLeaderboard] = useState();
  const [subgroupUserItem, setSubgroupUserItem] = useState();
  const [ownSubgroupId, setOwnSubgroupId] = useState();
  const [count, setCount] = useState();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [rankFieldGroup, setRankFieldGroup] = useState('points');
  const [commuting, setCommuting] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    trackEvent
  } = useAmplitudeTracker();
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  const {
    formatNumber
  } = useUnits();
  const {
    downloadXLSX
  } = useFileSaver();

  // Fetched once (not per tab switch) — the current employee's own site/team/region/subsidiary,
  // used to highlight their row and show their rank in the subgroups leaderboards.
  const {
    data: fetchedOwnGeogroups
  } = useUserGeogroupsQuery(partner?.geoGroupId ?? undefined);
  const ownGeogroups = partner?.geoGroupId ? fetchedOwnGeogroups : [];
  const isCustomChallenge = challenge?.type === 'custom';
  const rankFieldConfig = rankFieldGroupsConfig[rankFieldGroup];
  const hasCommutingVariant = !!rankFieldConfig.commutingField;
  const effectiveCommuting = hasCommutingVariant && commuting;
  const rankField = isCustomChallenge ? effectiveCommuting ? rankFieldConfig.commutingField : rankFieldConfig.field : undefined;
  useEffect(() => {
    if (initialized && challenge) {
      if (page > 1) setPage(1);
      getLeaderboard(challenge, 1);
    }
    setInitialized(true);
    return () => {
      cancelPromises();
    };
  }, [initialized, challenge, displayType, selectedSubgroup, rankField, ownGeogroups]);
  useEffect(() => {
    if (challenge && page > 1) getLeaderboard(challenge, page);
  }, [page]);
  async function getLeaderboard(challenge, page) {
    if (page === 1) {
      setIndividualLeaderboard(undefined);
      setUserChallenge(undefined);
      setSubgroupsLeaderboard(undefined);
      setSubgroupUserItem(undefined);
      setOwnSubgroupId(undefined);
      setCount(undefined);
    }
    setLoading(true);
    try {
      if (displayType === 'individual') {
        const showSubgroups = Boolean(!isHolding && (sites && sites.length > 0 || teams && teams.length > 0));
        const [{
          count,
          leaderboard: pageLeaderboard
        }, {
          leaderboard: [userItem]
        }] = await cancellablePromise(Promise.all([GeogroupService.getChallengeLeaderboard(challenge.id, {
          page,
          pageSize: isFree && !disableLeaderboardRestriction ? freeOfferEmployeesLeaderboardsMaxRowsCount : 50,
          ...(selectedSubgroup ? {
            subgroup: selectedSubgroup
          } : {}),
          addEmployeeData: showSubgroups,
          rankField
        }), currentUser && page === 1 ? GeogroupService.getChallengeLeaderboard(challenge.id, {
          page: 1,
          pageSize: 1,
          userId: currentUser.id,
          ...(selectedSubgroup ? {
            subgroup: selectedSubgroup
          } : {}),
          addEmployeeData: showSubgroups,
          rankField
        }) : {
          leaderboard: []
        }]));
        setCount(count);
        if (page === 1) setIndividualLeaderboard(pageLeaderboard);else setIndividualLeaderboard([...(individualLeaderboard || []), ...pageLeaderboard]);
        if (userItem?.leaderboardIndex !== undefined) setUserChallenge({
          rank: userItem.leaderboardIndex,
          progressValue: userItem.progressValue
        });else setUserChallenge(null);
      } else if (partner) {
        const leaderboard = await cancellablePromise(GeogroupService.getChallengeLeaderboardBySubGroups(partner, challenge.id, {
          subGroupDisplay: displayType
        }));
        setCount(leaderboard.length);
        setSubgroupsLeaderboard(isFree && !disableLeaderboardRestriction ? leaderboard.slice(0, freeOfferSubgroupsLeaderboardsMaxRowsCount) : leaderboard);

        // `ownGeogroups` loads separately (see the dedicated effect below) — until it's ready,
        // leave the "own" state as `undefined` (loading) rather than concluding "no membership"
        // (`null`), which would flash the rank card away and never come back once `ownGeogroups`
        // does resolve, since this same effect reruns when it changes and would just do it again.
        if (ownGeogroups) {
          const ownGroupId = ownGeogroups.find(({
            status,
            group
          }) => group.subGroupType === displayType && (displayType !== 'companySite' || status === 'manuallyJoin' || status === 'automaticallyJoin'))?.group.id;
          const ownItem = ownGroupId && leaderboard.find(({
            id
          }) => id === ownGroupId);
          setOwnSubgroupId(ownGroupId ?? null);
          setSubgroupUserItem(ownItem ? {
            progressValue: ownItem.progressValue,
            rank: ownItem.rank
          } : null);
        }
      }
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
    setLoading(false);
  }
  function handleSubgroupRowClick(id) {
    const allSubgroups = [...(sites || []), ...(teams || []), ...(regions || []), ...(subsidiaries || [])];
    const subgroup = allSubgroups.find(g => g.id === id);
    if (!subgroup) return;
    setSelectedSubgroup(subgroup);
    setDisplayType('individual');
  }
  async function handleXLSXDownload() {
    if (!partner || !challenge) return;
    trackEvent('Event Leaderboard Exported', {
      challenge_id: challenge.id,
      display_type: displayType,
      format: 'xlsx'
    });
    setDownloading(true);
    if (displayType === 'individual') {
      if (employee) {
        const allItems = [];
        const exportPageSize = isFree && !disableLeaderboardRestriction ? freeOfferEmployeesLeaderboardsMaxRowsCount : 50;
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const {
            leaderboard,
            hasMore: more
          } = await GeogroupService.getChallengeLeaderboard(challenge.id, {
            page,
            pageSize: exportPageSize,
            ...(selectedSubgroup ? {
              subgroup: selectedSubgroup
            } : {}),
            addEmployeeData: true,
            rankField
          });
          allItems.push(...leaderboard);
          hasMore = more && !(isFree && !disableLeaderboardRestriction);
          page++;
        }
        const {
          header: scoreHeader,
          formatValue: formatScore
        } = getRankFieldExportColumn(rankFieldGroup);
        downloadXLSX(`Challenge_${[challenge.title, partner.title].join('_').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '_')}_${moment().format('YYYY-MM-DD')}.xlsx`, ['Classement', 'Pseudo', ...(isHolding ? [] : ['Nom']), ...(isHolding ? [] : ['Prénom']), ...(isHolding || employee.role === 'employee' ? [] : ['Adresse email']), ...(isHolding ? [] : ['Site']), ...(isHolding ? [] : ['Équipe']), scoreHeader], allItems.map(({
          rank,
          username,
          firstName,
          lastName,
          professionalEmail,
          progressValue,
          subGroups
        }) => {
          const site = subGroups?.find(({
            type
          }) => type === 'companySite')?.title;
          const team = subGroups?.find(({
            type
          }) => type === 'companyTeam')?.title;
          return [rank, username || t('commons.news.deleted_user'), ...(isHolding ? [] : [lastName || '-']), ...(isHolding ? [] : [firstName || '-']), ...(isHolding || employee.role === 'employee' ? [] : [professionalEmail || '-']), ...(isHolding ? [] : [site || '-']), ...(isHolding ? [] : [team || '-']), isCustomChallenge ? formatScore(progressValue) : challenge.type === 'traveledDistance' ? (progressValue / 1000).toFixed(1) : progressValue];
        }));
      }
    } else if (subgroupsLeaderboard) {
      downloadXLSX(`Challenge_${[challenge.title, partner.title].join('_').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '_')}_${moment().format('YYYY-MM-DD')}.xlsx`, ['Classement', 'Nom', 'Score (pts)'], subgroupsLeaderboard.map(({
        rank,
        username,
        progressValue
      }) => [rank, username || t('commons.news.deleted_user'), challenge.type === 'traveledDistance' ? (progressValue / 1000).toFixed(1) : progressValue]));
    }
    setDownloading(false);
  }
  const exportAvailable = !!challenge && (displayType === 'individual' ? !!individualLeaderboard && individualLeaderboard.length > 0 : !!subgroupsLeaderboard && subgroupsLeaderboard.length > 0);
  function handleExportClick() {
    if (ExportDialog) setExportDialogOpen(true);else handleXLSXDownload();
  }
  useEffect(() => {
    if (!onExportReady) return;
    onExportReady(exportAvailable ? handleExportClick : undefined);
    return () => onExportReady(undefined);
  }, [onExportReady, exportAvailable, challenge, displayType, individualLeaderboard, subgroupsLeaderboard, rankField, ExportDialog]);
  const Button = _Button || _Button2;
  const isBttf = event.code?.toLowerCase() === 'bttf';
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingY: 3
  }, isBttf && /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    borderRadius: 4,
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    padding: 3,
    sx: {
      containerType: 'inline-size'
    },
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Box, {
    width: {
      '@': '100%',
      '@500': 'calc((100% - 16px) / 2)',
      '@800': 'calc((100% - 32px) / 4)'
    }
  }, /*#__PURE__*/React.createElement(StatCard, {
    bgColor: "#e4f2de",
    caption: t('commons.challenges.impact_points'),
    Icon: GlobeIcon,
    iconColor: "#268b1d",
    value: challenge ? formatNumber(Math.round(challenge.overralProgressValue)) : challenge === null ? 0 : undefined
  })), /*#__PURE__*/React.createElement(_Box, {
    width: {
      '@': '100%',
      '@500': 'calc((100% - 16px) / 2)',
      '@800': 'calc((100% - 32px) / 4)'
    }
  }, /*#__PURE__*/React.createElement(StatCard, {
    bgColor: "#e7f4fA",
    caption: t('commons.challenges.label_participants', {
      count: challenge ? challenge.nbMembers : 2
    }),
    Icon: PeopleIcon,
    iconColor: "#1589BB",
    value: challenge ? formatNumber(Math.round(challenge.nbMembers)) : challenge === null ? 0 : undefined
  }))), /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: 3,
    width: "100%"
  }, !onExportReady && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 3,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    variant: "h6"
  }, t('commons.challenges.navigation.leaderboard'))), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 2,
    justifyContent: "space-between",
    sx: displayTypes.length > 1 ? {
      borderBottom: '1px solid #e6e6ed'
    } : undefined
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 1
  }, displayTypes.length > 1 && /*#__PURE__*/React.createElement(_Tabs, {
    onChange: (_, value) => {
      setSelectedSubgroup(null);
      setDisplayType(value);
      trackEvent('Event intern ranking selected', {
        mod: displayTypeToMod[value]
      });
    },
    value: displayType
  }, displayTypes.map(key => /*#__PURE__*/React.createElement(_Tab, {
    key: key,
    label: t(displayTypesMap[key].labelKey),
    sx: {
      color: '#2C2E30',
      fontSize: 16,
      fontWeight: 500,
      minHeight: 48,
      textTransform: 'initial'
    },
    value: key
  }))), selectedSubgroup && /*#__PURE__*/React.createElement(_Chip, {
    deleteIcon: /*#__PURE__*/React.createElement(_Close, null),
    label: selectedSubgroup.title,
    onDelete: () => setSelectedSubgroup(null),
    size: "small"
  })), !onExportReady && (displayType === 'individual' ? !individualLeaderboard || individualLeaderboard.length > 0 : !subgroupsLeaderboard || subgroupsLeaderboard.length > 0) && /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: downloading || !challenge || (displayType === 'individual' ? !individualLeaderboard : !subgroupsLeaderboard),
    onClick: handleExportClick,
    size: "medium",
    startIcon: downloading ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(ExportIcon, null),
    variant: "text"
  }, t('commons.actions.export'))), isCustomChallenge && displayType === 'individual' && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "flex-start",
    display: "flex",
    flexWrap: "wrap",
    gap: 2
  }, /*#__PURE__*/React.createElement(_FormControl, {
    margin: "none",
    sx: rankFieldDropdownSx
  }, /*#__PURE__*/React.createElement(_Select, {
    MenuProps: rankFieldDropdownMenuProps,
    onChange: ({
      target: {
        value
      }
    }) => {
      const nextRankFieldGroup = value;
      const nextEffectiveCommuting = !!rankFieldGroupsConfig[nextRankFieldGroup].commutingField && commuting;
      setRankFieldGroup(nextRankFieldGroup);
      trackEvent('Custom challenge event score selection changed', {
        rank_field: nextRankFieldGroup,
        trip_type: nextEffectiveCommuting ? 'commuting' : 'all'
      });
    },
    renderValue: value => /*#__PURE__*/React.createElement(_Typography, {
      noWrap: true,
      component: "span",
      variant: "body2"
    }, t('commons.challenges.leaderboard.rank_field_sort_by'), ' : ', /*#__PURE__*/React.createElement(_Typography, {
      component: "span",
      fontWeight: 700,
      variant: "body2"
    }, t(rankFieldGroupsConfig[value].labelKey))),
    value: rankFieldGroup
  }, rankFieldGroups.map(key => /*#__PURE__*/React.createElement(_MenuItem, {
    key: key,
    value: key
  }, t(rankFieldGroupsConfig[key].labelKey))))), /*#__PURE__*/React.createElement(_FormControl, {
    disabled: !hasCommutingVariant,
    margin: "none",
    sx: rankFieldDropdownSx
  }, /*#__PURE__*/React.createElement(_Select, {
    MenuProps: rankFieldDropdownMenuProps,
    onChange: ({
      target: {
        value
      }
    }) => {
      setCommuting(value === 'commuting');
      trackEvent('Custom challenge event score selection changed', {
        rank_field: rankFieldGroup,
        trip_type: value
      });
    },
    renderValue: value => /*#__PURE__*/React.createElement(_Typography, {
      noWrap: true,
      component: "span",
      variant: "body2"
    }, t('commons.challenges.leaderboard.rank_field_trips_label'), ' : ', /*#__PURE__*/React.createElement(_Typography, {
      component: "span",
      fontWeight: 700,
      variant: "body2"
    }, t(value === 'commuting' ? 'commons.challenges.leaderboard.rank_field_commuting_trips' : 'commons.challenges.leaderboard.rank_field_all_trips'))),
    value: effectiveCommuting ? 'commuting' : 'all'
  }, /*#__PURE__*/React.createElement(_MenuItem, {
    value: "all"
  }, t('commons.challenges.leaderboard.rank_field_all_trips')), /*#__PURE__*/React.createElement(_MenuItem, {
    value: "commuting"
  }, t('commons.challenges.leaderboard.rank_field_commuting_trips'))))), displayType === 'individual' ? /*#__PURE__*/React.createElement(IndividualLeaderboard, {
    Button: _Button,
    count: count,
    disableLeaderboardRestriction: disableLeaderboardRestriction,
    event: event,
    FakeItemsCard: FakeItemsCard,
    freeOfferEmployeesLeaderboardsMaxRowsCount: freeOfferEmployeesLeaderboardsMaxRowsCount,
    isFree: isFree,
    isHolding: isHolding,
    leaderboard: individualLeaderboard,
    loading: loading,
    page: page,
    rankField: rankField,
    rankFieldLabel: isCustomChallenge ? t(rankFieldConfig.labelKey) : undefined,
    setPage: setPage,
    sites: sites,
    teams: teams,
    userChallenge: userChallenge
  }) : /*#__PURE__*/React.createElement(SubgroupsLeaderboard, {
    count: count,
    disableLeaderboardRestriction: disableLeaderboardRestriction,
    displayType: displayType,
    event: event,
    FakeItemsCard: FakeItemsCard,
    freeOfferSubgroupsLeaderboardsMaxRowsCount: freeOfferSubgroupsLeaderboardsMaxRowsCount,
    isFree: isFree,
    leaderboard: subgroupsLeaderboard,
    onRowClick: sites || teams || regions || subsidiaries ? handleSubgroupRowClick : undefined,
    ownGroupId: ownSubgroupId,
    ownGroupItem: subgroupUserItem
  })), ExportDialog && challenge && /*#__PURE__*/React.createElement(ExportDialog, {
    challenge: challenge,
    displayType: displayType,
    handleXLSXDownload: handleXLSXDownload,
    leaderboard: (displayType === 'individual' ? individualLeaderboard : subgroupsLeaderboard) || [],
    onClose: () => setExportDialogOpen(false),
    open: exportDialogOpen,
    rankField: rankField,
    rankFieldLabel: isCustomChallenge ? t(rankFieldConfig.labelKey) : undefined,
    subgroup: selectedSubgroup ?? undefined,
    userChallenge: null
  }));
}
export { fakeSites, fakeTeams, fakeRegions };
export default CompanyLeaderboard;